import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { InvoiceRecord, Locale } from '@worki/shared';
import { formatDate, formatDateTime } from '../lib/format.js';

/** One labelled line of an invoice's details. */
export function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2">
      <dt className="text-xs text-muted-foreground shrink-0">{label}</dt>
      <dd className="text-xs font-medium truncate">{children}</dd>
    </div>
  );
}

/**
 * INV-011 — the dates an invoice carries: the one it prints, and under it the
 * day its mail came.
 *
 * The import timestamp has its own label so it cannot be confused with the
 * document date or mail arrival. An invoice imported without mail has no mail date.
 */
export function InvoiceDateRows({ invoice, locale }: { invoice: InvoiceRecord; locale: Locale }) {
  const { t } = useTranslation();
  return (
    <>
      <DetailRow label={t('invoices.issuedAt')}>
        <span className="ltr-nums">{formatDate(invoice.issuedAt, locale)}</span>
      </DetailRow>
      {invoice.receivedAt && (
        <DetailRow label={t('invoices.receivedAt')}>
          <span className="flex flex-col items-end ltr-nums">
            <span>{formatDate(invoice.receivedAt, locale)}</span>
            <time dateTime={invoice.receivedAt} className="text-muted-foreground">
              {new Intl.DateTimeFormat(locale === 'he' ? 'he-IL' : 'en-US', { hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(invoice.receivedAt))}
            </time>
          </span>
        </DetailRow>
      )}
      <DetailRow label={t('invoices.addedAt')}>
        <time dateTime={invoice.createdAt} className="ltr-nums">{formatDateTime(invoice.createdAt, locale)}</time>
      </DetailRow>
    </>
  );
}
